import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getOAuthClients } from '../lib/api';
import { sanitizeReturnTo } from '../lib/return-to';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSessionAndRoute = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setError('Failed to verify session. Please try signing in again.');
          return;
        }

        if (!session) {
          navigate('/login', { replace: true });
          return;
        }

        // Resume an interrupted flow (e.g. OAuth consent) when a safe relative
        // returnTo is present — skips the first-time/welcome routing below.
        const returnTo = sanitizeReturnTo(searchParams.get('returnTo'));
        if (returnTo) {
          navigate(returnTo, { replace: true });
          return;
        }

        // Check if user has OAuth clients
        const clientsRes = await getOAuthClients(session.access_token);
        const clientsData = await clientsRes.json() as { clients?: unknown[] };
        const hasClients = clientsData.clients && clientsData.clients.length > 0;

        // Route first-time users (no clients) to welcome, returning users to dashboard
        if (!hasClients) {
          navigate('/welcome', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError('Something went wrong. Please try signing in again.');
        console.error('Auth callback error:', err);
      }
    };

    checkSessionAndRoute();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="text-sentinel-700 hover:underline"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sentinel-700 mb-4"></div>
        <p className="text-gray-600">Verifying your account...</p>
      </div>
    </div>
  );
}
